import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { ViewModel } from '../../../models/view.model';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { isFirstRenderDonePFJ } from '../state/UIStateJ';
import { selectedViewIdPFJ, viewsPFJ } from '../state/ViewStateJ';

/**
 * Syncs the given viewId into state (`selectedViewIdPFJ`).
 * Only sets state when the viewId is valid and exists in the user's available views.
 * Invalid/missing viewIds are a no-op here — PortfolioOverview's render guards
 * handle those cases (Forbidden / Not Found).
 * Also suspends on `viewsPFJ`, ensuring views are loaded before the component renders.
 */
export function useSyncSelectedView(viewId: number) {
  const views = useAtomValue(viewsPFJ);
  const [selectedViewId, setSelectedViewId] = useAtom(selectedViewIdPFJ);
  const setIsFirstRenderDone = useSetAtom(isFirstRenderDonePFJ);

  useEffect(() => {
    const viewExists = views.some((view) => view.id === viewId);
    if (!viewExists) {
      return;
    }

    if (viewId !== selectedViewId) {
      setIsFirstRenderDone(false);
      setSelectedViewId(viewId);
    }
  }, [viewId, selectedViewId, setIsFirstRenderDone, setSelectedViewId, views]);
}

export function getViewOrDefault(viewId: number | undefined, views: ViewModel[]) {
  if (views.length === 0) {
    return undefined;
  }

  if (viewId != null && !Number.isNaN(viewId)) {
    const view = views.find((view) => view.id === viewId);
    if (view) {
      return view;
    }
  }

  return views[0];
}

export function useDefaultPortfolioView() {
  const defaultViewPref = PreferencesServiceV3.get().getPreference<number>(
    getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
    PreferenceScope.ORGANIZATION
  );
  const views = useAtomValue(viewsPFJ);
  return getViewOrDefault(defaultViewPref, views);
}
